"use client";

import { useForm } from "react-hook-form";
import useMutation from "swr/mutation";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useParams, useSearchParams } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRef, useState } from "react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";

import otpLocale from "@/locales/otp.json";
import submissionLocale from "@/locales/submission.json";
import useLocaleLegacy from "@/hooks/use-locale-legacy";
import { fetcher } from "@/lib/fetcher";
import { toast } from "sonner";
import getErrorMessage from "@/lib/get-error";
import formatTime from "@/lib/format-time";
import useCleanup from "@/hooks/use-cleanup";
import { utm_query_params } from "@/app/models/utm";
import { useOTP } from "@/hooks/use-otp";

export interface SubmissionProps {
  title: string;
  description: string;
  fields: {
    id: string;
    text: string;
    name: string;
    placeholder: string;
    type: string;
    required: boolean;
  }[];
}

export default function Submission(props: SubmissionProps) {
  const params = useParams();
  const qs = useSearchParams();
  const cleanup = useCleanup();

  const { t } = useLocaleLegacy(otpLocale);
  const { t: submission } = useLocaleLegacy(submissionLocale);

  const otp = useRef("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [timer, setTimer] = useState(0);

  const form = useForm();

  const { sendOTP, validateOTP } = useOTP();

  const { trigger: submitContactForm } = useMutation(
    "/nxapi/contact-forms",
    async function (url, { arg }: { arg: Record<string, any> }) {
      return await fetcher(url, {
        method: "POST",
        body: JSON.stringify(arg),
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
          "Content-Type": "application/json",
        },
      });
    }
  );

  const onSubmit = (values: Record<string, any>) => {
    sendOTP({
      phone: values.phone_number,
    }).catch((err) => {
      toast.error(String(getErrorMessage(err).error));
    });
    setShowOTP(true);
  };

  const onResendOTP = () => {
    sendOTP({
      phone: form.getValues().phone_number,
    }).then((res) => {
      setTimer(res.countdown_in_seconds);
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev - 1 < 0) {
            clearInterval(interval);
            return prev;
          }
          return prev - 1;
        });
      }, 1_000); //1seconds;

      cleanup(() => clearInterval(interval));
    })
    .catch(err =>{
      toast.error(String(getErrorMessage(err).error));
    })
  };

  const onSendOTP = async () => {
    try {
      const utmParams = utm_query_params.reduce((acc, key) => {
        if (!qs?.has(key)) return acc;
        return {
          ...acc,
          [key]: qs.get(key) || "",
        };
      }, {});

      const response = await validateOTP({
        phone: form.getValues().phone_number,
        otp_code: otp.current,
      });

      if (response.status === "error") {
        throw new Error(response.message);
      }

      await submitContactForm({
        phone: form.getValues().phone_number,
        full_name: form.getValues().name,
        email: form.getValues().email,
        sales: params?.slug ?? "",
        ...utmParams,
      });
      form.reset();
      setShowOTP(false)
      setShowSuccess(true);
    } catch (error) {
      toast.error(String(getErrorMessage(error).error));
    }
  };

  return (
    <>
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("success")}</DialogTitle>
            <DialogDescription>{t("success_description")}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={showOTP} onOpenChange={setShowOTP}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">{t("title")}</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col items-center gap-y-3">
                <p>{t("description")}</p>

                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                  className="mt-4"
                  onChange={(e) => {
                    otp.current = e;
                  }}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>

                <button
                  className="rounded-sm px-4 py-1 bg-gold text-white cursor-pointer"
                  onClick={onSendOTP}
                >
                  {t("verify_btn")}
                </button>
                <div>
                  {t("receive_otp")}
                  <button
                    disabled={timer > 0}
                    className="ml-1 cursor-pointer disabled:cursor-not-allowed underline"
                    onClick={onResendOTP}
                  >
                    {t("resend_otp")}

                    {timer > 0 && <span> ({formatTime(timer)})</span>}
                  </button>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div className="py-12 xl:pb-20 xl:pt-14 bg-darkgold">
        <div className="w-full xl:max-w-screen-xl mx-auto">
          <h1
            className="text-white font-bold text-center text-sm xl:text-3xl font-pluto"
            dangerouslySetInnerHTML={{ __html: props.title }}
          ></h1>
          <h2
            className="text-white text-center text-sm xl:text-3xl mt-2"
            dangerouslySetInnerHTML={{ __html: props.description }}
          ></h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col xl:flex-row justify-center gap-y-3 xl:gap-y-0 xl:gap-x-2 mt-8 px-10 xl:px-0">
                {props.fields.map((field) => {
                  return (
                    <FormField
                      control={form.control}
                      name={field.name}
                      key={field.id}
                      rules={{
                        required: `${field.text} is required`,
                      }}
                      render={({ field: filedInput }) => (
                        <FormItem>
                          <FormControl>
                            <input
                              className="xl:rounded-[3.3rem] text-white placeholder:text-white border-b xl:border border-gold py-3 xl:px-5 text-[13px] xl:text-xl focus:outline-none"
                              autoComplete="nope"
                              {...filedInput}
                              onKeyDown={(e) => {
                                if (field.type === "tel") {
                                  if (
                                    [
                                      "Backspace",
                                      "Delete",
                                      "Tab",
                                      "Escape",
                                      "Enter",
                                      "ArrowLeft",
                                      "ArrowRight",
                                      "ArrowUp",
                                      "ArrowDown",
                                    ].indexOf(e.key) !== -1 ||
                                    // Allow: Ctrl/Cmd+A,C,V,X,Z (for copy/paste/select all)
                                    ((e.ctrlKey || e.metaKey) &&
                                      ["a", "c", "v", "x", "z"].includes(
                                        e.key.toLowerCase()
                                      ))
                                  ) {
                                    return; // let it happen
                                  }
                                  // Block anything that's not 0–9
                                  if (!/^[0-9]$/.test(e.key)) {
                                    e.preventDefault();
                                    return;
                                  }
                                }
                              }}
                              type={field.type}
                              placeholder={field.placeholder}
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  );
                })}
                <button
                  type="submit"
                  className="font-bold h-8 xl:h-auto w-32 xl:w-48 p-0 rounded-[3.3rem] font-pluto bg-gold text-white cursor-pointer mx-auto xl:mx-0 text-[12px] xl:text-2xl mt-6 xl:mt-0"
                >
                  {submission("submit_btn_label")}
                </button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
}
